/* FamilyMemberPanel — modal that opens when a family member is tapped
   in the tree or the family list (admin + public dashboard views).

   VIEW mode: name, photo, relationship, status, dates, notes,
   linked-memorial actions.
   EDIT mode (canEdit): full editing — names, relationship, living status,
   dates, email/phone, location, resting place, notes, tree position,
   visibility, photo upload/replace/remove — plus Save / Cancel / Delete. */
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ExternalLink, Plus, Leaf as LeafIcon, Pencil, Trash2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../lib/api';
import { RelationshipSelector, RELATIONSHIP_PRESETS, resolveRelationship } from './RelationshipSelector';
import { TREE_POSITIONS } from './FamilyTreeView';

const LEAF_GREEN = '#5C8B49';
const LEAF_GOLD = '#C5A059';

const isLiving = (m) =>
  (typeof m.is_living === 'boolean') ? m.is_living : !m.death_date;

const yearOf = (s) => (typeof s === 'string' && s.length >= 4 ? s.slice(0, 4) : null);

function splitName(name) {
  const parts = (name || '').trim().split(/\s+/);
  if (parts.length <= 1) return { first: parts[0] || '', last: '' };
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] };
}

export function FamilyMemberPanel({
  member,
  open,
  onClose,
  canCreate = false,
  canEdit = false,
  onChanged,
  sourceMemorialId,
  sourceMemorialName,
}) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  if (!open || !member) return null;

  const living = isLiving(member);
  const by = yearOf(member.birth_date);
  const dy = yearOf(member.death_date);
  const dateLine = by
    ? (dy ? `${by} – ${dy}` : `Born ${by} · Living`)
    : (dy ? `In memory · ${dy}` : (living ? 'Living' : 'In memory'));

  const handleCreate = () => {
    navigate('/dashboard/new', {
      state: {
        prefill: {
          full_name: member.name,
          date_of_birth: member.birth_date || '',
          date_of_passing: member.death_date || '',
        },
        linked_from: {
          memorial_id: sourceMemorialId,
          memorial_name: sourceMemorialName,
          family_id: member.family_id,
          relationship: member.relationship,
        },
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="family-member-panel-backdrop"
    >
      <div
        className="bg-[hsl(var(--background))] w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-[hsl(var(--primary)/0.18)] shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="family-member-panel"
      >
        <div className="flex justify-between items-center p-3">
          {canEdit && !editing ? (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--primary))] border border-[hsl(var(--primary)/0.25)] hover:bg-[hsl(var(--primary)/0.06)]"
              data-testid="family-member-edit-btn"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
          ) : <span />}
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-[hsl(var(--primary)/0.06)] flex items-center justify-center"
            data-testid="family-member-panel-close"
            aria-label="Close"
          >
            <X className="w-4 h-4 text-[hsl(var(--primary))]" />
          </button>
        </div>

        {editing ? (
          <EditForm
            member={member}
            onCancel={() => setEditing(false)}
            onSaved={() => { setEditing(false); onChanged?.(); }}
            onDeleted={() => { setEditing(false); onClose?.(); onChanged?.(); }}
          />
        ) : (
          <>
            <div className="flex justify-center -mt-2">
              <div
                className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center font-serif text-3xl"
                style={{
                  backgroundColor: 'rgba(26, 54, 38, 0.06)',
                  color: '#1A3626',
                  border: `3px solid ${living ? LEAF_GREEN : LEAF_GOLD}`,
                }}
              >
                {member.photo_url
                  ? <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
                  : <span>{(member.name || '?').trim().charAt(0).toUpperCase()}</span>
                }
              </div>
            </div>

            <div className="px-6 pt-4 pb-2 text-center">
              <h3 className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="family-member-panel-name">
                {member.name}
              </h3>
              <div className="mt-1 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary)/0.6)]">
                {member.relationship || 'Family'}
              </div>
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.14em] uppercase font-medium"
                style={{ color: living ? LEAF_GREEN : LEAF_GOLD }}
                data-testid="family-member-panel-status"
              >
                <LeafIcon className="w-3.5 h-3.5" fill="currentColor" />
                {dateLine}
              </div>
              {canEdit && (member.email || member.phone) && (
                <div className="mt-2 text-xs text-[hsl(var(--primary)/0.6)]">
                  {member.email}{member.email && member.phone ? ' · ' : ''}{member.phone}
                </div>
              )}
            </div>

            {member.notes && (
              <p className="px-6 pb-2 text-sm italic font-serif text-[hsl(var(--primary)/0.75)] text-center leading-relaxed">
                {member.notes}
              </p>
            )}

            <div className="px-6 pb-6 pt-4">
              {member.linked_memorial_slug ? (
                <a
                  href={`/m/${member.linked_memorial_slug}`}
                  className="ml-btn-primary w-full inline-flex items-center justify-center gap-2"
                  data-testid="family-member-panel-view-memorial"
                >
                  <ExternalLink className="w-4 h-4" />
                  View memorial
                </a>
              ) : canCreate ? (
                <>
                  <button
                    type="button"
                    onClick={handleCreate}
                    className="ml-btn-primary w-full inline-flex items-center justify-center gap-2"
                    data-testid="family-member-panel-create-memorial"
                  >
                    <Plus className="w-4 h-4" />
                    Create memorial for {member.name.split(' ')[0] || 'this person'}
                  </button>
                  <p className="mt-3 text-[11px] tracking-[0.14em] uppercase text-center text-[hsl(var(--primary)/0.55)]">
                    Pre-fills the wizard — finish + pay to publish
                  </p>
                </>
              ) : (
                <div className="text-center text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.45)]">
                  No memorial yet
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditForm({ member, onCancel, onSaved, onDeleted }) {
  const { first, last } = splitName(member.name);
  const presetValues = (RELATIONSHIP_PRESETS?.family || []).map((r) => r.value);
  const relLower = (member.relationship || '').trim().toLowerCase();
  const relIsPreset = presetValues.includes(relLower);
  const [form, setForm] = useState({
    first_name: first,
    last_name: last,
    relationship: relIsPreset ? relLower : (member.relationship ? 'other' : ''),
    relationship_other: relIsPreset ? '' : (member.relationship || ''),
    is_living: isLiving(member),
    birth_date: member.birth_date || '',
    death_date: member.death_date || '',
    email: member.email || '',
    phone: member.phone || '',
    location: member.location || '',
    resting_place: member.resting_place || '',
    notes: member.notes || '',
    tree_position: member.tree_position || 'auto',
    visibility: member.visibility || 'public',
  });
  const [photoPreview, setPhotoPreview] = useState(member.photo_url || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickPhoto = (f) => {
    if (!f) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Photo must be a JPG, PNG, or WEBP image.');
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error('Photo must be 8 MB or smaller.');
      return;
    }
    setPhotoFile(f);
    setRemovePhoto(false);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    const name = `${form.first_name.trim()} ${form.last_name.trim()}`.trim();
    if (!name) { toast.error('Name is required.'); return; }
    const relationship = resolveRelationship(form.relationship, form.relationship_other);
    if (!relationship) { toast.error('Relationship is required.'); return; }
    setSaving(true);
    try {
      await api.patch(`/family/${member.family_id}`, {
        name,
        relationship,
        is_living: form.is_living,
        birth_date: form.birth_date || null,
        death_date: form.is_living ? null : (form.death_date || null),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        location: form.location.trim() || null,
        resting_place: form.resting_place.trim() || null,
        notes: form.notes.trim() || null,
        tree_position: form.tree_position,
        visibility: form.visibility,
      });
      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        await api.post(`/family/${member.family_id}/photo`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
      } else if (removePhoto && member.photo_url) {
        await api.delete(`/family/${member.family_id}/photo`);
      }
      toast.success('Family member updated.');
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${member.name} from the family? This cannot be undone.`)) return;
    try {
      await api.delete(`/family/${member.family_id}`);
      toast.success(`${member.name} removed.`);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="px-6 pb-6" data-testid="family-member-edit-form">
      {/* Photo */}
      <div className="flex flex-col items-center gap-2">
        <div
          className="w-24 h-24 rounded-full overflow-hidden flex items-center justify-center font-serif text-3xl border-2 border-[hsl(var(--accent)/0.5)]"
          style={{ backgroundColor: 'rgba(26,54,38,0.06)', color: '#1A3626' }}
        >
          {photoPreview && !removePhoto
            ? <img src={photoPreview} alt="" className="w-full h-full object-cover" data-testid="family-photo-preview" />
            : <span>{(form.first_name || '?').trim().charAt(0).toUpperCase()}</span>}
        </div>
        <input
          ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => pickPhoto(e.target.files?.[0])}
          data-testid="family-photo-input"
        />
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--primary))] hover:underline"
                  data-testid="family-photo-upload-btn">
            <Camera className="w-3.5 h-3.5" />
            {photoPreview && !removePhoto ? 'Replace photo' : 'Upload photo'}
          </button>
          {(photoPreview && !removePhoto) && (
            <button type="button"
                    onClick={() => { setRemovePhoto(true); setPhotoFile(null); }}
                    className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[hsl(var(--destructive))] hover:underline"
                    data-testid="family-photo-remove-btn">
              Remove
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-5">
        <input className="ml-input" placeholder="First name" value={form.first_name}
               onChange={(e) => set('first_name', e.target.value)} data-testid="family-edit-first-name" />
        <input className="ml-input" placeholder="Last name" value={form.last_name}
               onChange={(e) => set('last_name', e.target.value)} data-testid="family-edit-last-name" />
        <div className="col-span-2">
          <RelationshipSelector
            preset="family"
            value={form.relationship}
            customValue={form.relationship_other}
            onChange={(rel, other) => setForm((f) => ({ ...f, relationship: rel, relationship_other: other }))}
            placeholder="Relationship…"
            selectTestId="family-edit-rel"
            inputTestId="family-edit-rel-other"
          />
        </div>
        <label className="col-span-2 inline-flex items-center gap-2 cursor-pointer select-none text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--primary)/0.75)]">
          <input type="checkbox" className="w-4 h-4 accent-[hsl(var(--secondary))]"
                 checked={form.is_living}
                 onChange={(e) => set('is_living', e.target.checked)}
                 data-testid="family-edit-living" />
          Still living
        </label>
        <label className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.55)]">
          Born
          <input type="date" className="ml-input mt-1" value={form.birth_date}
                 onChange={(e) => set('birth_date', e.target.value)} data-testid="family-edit-birth" />
        </label>
        <label className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.55)]">
          Passed
          <input type="date" className="ml-input mt-1" value={form.death_date} disabled={form.is_living}
                 onChange={(e) => set('death_date', e.target.value)} data-testid="family-edit-death" />
        </label>
        <input className="ml-input" type="email" placeholder="Email (private)" value={form.email}
               onChange={(e) => set('email', e.target.value)} data-testid="family-edit-email" />
        <input className="ml-input" type="tel" placeholder="Phone (private)" value={form.phone}
               onChange={(e) => set('phone', e.target.value)} data-testid="family-edit-phone" />
        <input className="ml-input col-span-2" placeholder="Location (where they live)" value={form.location}
               onChange={(e) => set('location', e.target.value)} data-testid="family-edit-location" />
        {!form.is_living && (
          <input className="ml-input col-span-2" placeholder="Resting place / cemetery" value={form.resting_place}
                 onChange={(e) => set('resting_place', e.target.value)} data-testid="family-edit-resting" />
        )}
        <textarea rows={2} className="ml-input col-span-2" placeholder="Notes" value={form.notes}
                  onChange={(e) => set('notes', e.target.value)} data-testid="family-edit-notes" />
        <label className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.55)]">
          Tree position
          <select className="ml-input mt-1 normal-case tracking-normal" value={form.tree_position}
                  onChange={(e) => set('tree_position', e.target.value)} data-testid="family-edit-tree-position">
            {TREE_POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </label>
        <label className="text-[10px] tracking-[0.2em] uppercase text-[hsl(var(--primary)/0.55)]">
          Visibility
          <select className="ml-input mt-1 normal-case tracking-normal" value={form.visibility}
                  onChange={(e) => set('visibility', e.target.value)} data-testid="family-edit-visibility">
            <option value="public">Public — shown on the memorial</option>
            <option value="private">Private — family dashboard only</option>
          </select>
        </label>
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button type="button" onClick={save} disabled={saving}
                className="ml-btn-primary flex-1" data-testid="family-edit-save">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving}
                className="ml-btn-outline" data-testid="family-edit-cancel">
          Cancel
        </button>
        <button type="button" onClick={remove} disabled={saving}
                className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-[hsl(var(--destructive))] hover:underline ml-auto"
                data-testid="family-edit-delete">
          <Trash2 className="w-3.5 h-3.5" /> Delete
        </button>
      </div>
    </div>
  );
}

export default FamilyMemberPanel;
